/**
 * Utility functions for tracking and displaying workflow last usage
 */

const STORAGE_KEY = "workflowLastUsage";

/**
 * Track that a workflow was just used
 * @param {string} workflowId - The ID of the workflow
 */
export function trackWorkflowUsage(workflowId) {
    if (!workflowId) return;

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const usageData = stored ? JSON.parse(stored) : {};

        usageData[workflowId] = new Date().toISOString();

        localStorage.setItem(STORAGE_KEY, JSON.stringify(usageData));
    } catch (error) {
        console.error("Error tracking workflow usage:", error);
    }
}

/**
 * Get the last usage date for a workflow
 * @param {string} workflowId - The ID of the workflow
 * @returns {Date|null} The last usage date or null if never used
 */
export function getWorkflowLastUsage(workflowId) {
    if (!workflowId) return null;

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;

        const usageData = JSON.parse(stored);
        const lastUsed = usageData[workflowId];

        return lastUsed ? new Date(lastUsed) : null;
    } catch (error) {
        console.error("Error getting workflow last usage:", error);
        return null;
    }
}

/**
 * Get a friendly relative time string for when a workflow was last used
 * @param {string} workflowId - The ID of the workflow
 * @returns {string|null} A friendly message like "used two days ago" or null if never used
 */
export function getWorkflowLastUsageMessage(workflowId) {
    const lastUsed = getWorkflowLastUsage(workflowId);
    if (!lastUsed) return null;

    return formatRelativeTime(lastUsed);
}

/**
 * Format a date as a friendly relative time string
 * @param {Date} date - The date to format
 * @returns {string} A friendly message like "used just now", "used 2 hours ago", etc.
 */
export function formatRelativeTime(date) {
    if (!date || !(date instanceof Date)) return null;

    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const diffYear = Math.floor(diffDay / 365);

    // Just now (less than 1 minute)
    if (diffSec < 60) {
        return "used just now";
    }

    // Minutes ago
    if (diffMin < 60) {
        if (diffMin === 1) {
            return "used a minute ago";
        }
        return `used ${diffMin} minutes ago`;
    }

    // Hours ago
    if (diffHour < 24) {
        if (diffHour === 1) {
            return "used an hour ago";
        }
        return `used ${diffHour} hours ago`;
    }

    // Days ago
    if (diffDay < 7) {
        if (diffDay === 1) {
            return "used yesterday";
        }
        return `used ${diffDay} days ago`;
    }

    // Weeks ago
    if (diffWeek < 4) {
        if (diffWeek === 1) {
            return "used a week ago";
        }
        return `used ${diffWeek} weeks ago`;
    }

    // Months ago
    if (diffMonth < 12) {
        if (diffMonth === 1) {
            return "used a month ago";
        }
        return `used ${diffMonth} months ago`;
    }

    // Years ago
    if (diffYear === 1) {
        return "used a year ago";
    }
    return `used ${diffYear} years ago`;
}
